import useToast from "hooks/common/useToast";
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ToastPayload } from "types/toast";
import { LazyMotion, m } from "framer-motion";

const loadFeatures = () => import('types/motionFeature').then((res) => res.default);

const Toast = ({ id, toast }: { id: string, toast: ToastPayload }) => {
    const { remove } = useToast();
    const { title, text, options, mountTo } = toast;
    const { duration, type, onClick } = options;

    const backgroundColor = useMemo(() => {
      switch (type) {
        case 'default':
        default:
          return 'bg-toast-bg-default';
        case 'lovematch':
          return 'bg-toast-bg-lovematch';
        case 'alert':
          return 'bg-toast-bg-alert';
      }
    }, [type]);

    const textColor = useMemo(() => {
      switch (type) {
        case 'default':
        default:
          return 'text-toast-txt-default';
        case 'lovematch':
          return 'text-toast-txt-lovematch';
        case 'alert':
          return 'text-toast-txt-alert';
      }
    }, [type]);

    useEffect(() => {
      const timer = setTimeout(() => {
        remove(id);
      }, duration);
      return () => clearTimeout(timer);
    }, [id, duration, remove]);

    const portalRoot = useMemo(() => mountTo ?? (typeof document !== 'undefined' ? document.getElementById('hMainContainer') : null), [mountTo]);
    if (!portalRoot) return null;

    const content = (
      <LazyMotion features={loadFeatures} strict>
        <m.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 32 }}
          data-component="Toast"
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={`absolute left-4 right-4 top-4 ${backgroundColor} ${textColor} rounded-lg p-3 flex flex-col gap-1`}
          style={{ zIndex: 102 }}
          onClick={onClick}
        >
          {title && <div className="typography-headline-md">{title}</div>}
          <span className="typography-body-sm">{text}</span>
        </m.div>
      </LazyMotion>
    );

    return createPortal(content, portalRoot);
};

export default Toast;